import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PlayerAppearance } from '@sorare/core/src/components/scoring/PlayerAppearance';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { Cell } from 'components/searchPlayerTable/SearchTable/ui';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import type { WithPlayerAppearancesCell_anyPlayer } from './__generated__/index.graphql';

type Props = {
  children: ReactNode;
  anyPlayer: WithPlayerAppearancesCell_anyPlayer;
  mode: TotalAveragePlayerScore;
};
const isAppearanceMode = (
  mode: TotalAveragePlayerScore
): mode is
  | typeof AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE
  | typeof AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE
  | typeof AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
  | typeof AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE => {
  return [
    AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
    AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE,
    AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
  ].includesValue(mode);
};

export const WithPlayerAppearancesCell = ({
  children,
  mode,
  anyPlayer,
}: Props) => {
  const { statsManager } = useManagePlayerSearchView();
  const showAppearances =
    statsManager[PlayerSearchViewAttributes.SHOW_APPEARANCES];
  const appearancesMode = isAppearanceMode(mode) ? mode : undefined;
  return (
    <Cell>
      <Vertical center gap={0.5}>
        {children}
        {!!appearancesMode && !!showAppearances && (
          <PlayerAppearance
            withTooltip
            player={anyPlayer}
            mode={appearancesMode}
          />
        )}
      </Vertical>
    </Cell>
  );
};

WithPlayerAppearancesCell.fragments = {
  anyPlayer: gql`
    fragment WithPlayerAppearancesCell_anyPlayer on AnyPlayerInterface {
      slug
      ...PlayerAppearance_anyPlayer
    }
    ${PlayerAppearance.fragments.anyPlayer}
  ` as TypedDocumentNode<WithPlayerAppearancesCell_anyPlayer>,
};
